import { useMutation, useQuery, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { Check, History, Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import {
  StoredRecommendation,
  listRecentRecommendations,
  markRecommendationCompleted,
  rateRecommendation,
} from '../services/dailyRecommendation';
import { localDate } from '../utils/date';

const todayIso = () => localDate();

/**
 * The last two weeks of prescribed plans with the athlete's feedback.
 * Answers here ("did you do it?", thumbs) feed tomorrow's coach context.
 */
export function PlanHistory() {
  const queryClient = useQueryClient();
  const { data } = useQuery('plan-history', () => listRecentRecommendations(14), {
    staleTime: 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries('plan-history');
  const rate = useMutation(
    ({ day, rating }: { day: string; rating: 'up' | 'down' | null }) =>
      rateRecommendation(day, rating),
    { onSuccess: invalidate }
  );
  const complete = useMutation(
    ({ day, completed }: { day: string; completed: boolean | null }) =>
      markRecommendationCompleted(day, completed),
    { onSuccess: invalidate }
  );

  // Today's plan has its own feedback controls on the workout card.
  const past = (data ?? []).filter((p) => p.day !== todayIso());
  if (past.length === 0) return null;

  const toggleRating = (p: StoredRecommendation, rating: 'up' | 'down') =>
    rate.mutate({ day: p.day, rating: p.rating === rating ? null : rating });
  const toggleCompleted = (p: StoredRecommendation, completed: boolean) =>
    complete.mutate({ day: p.day, completed: p.completed === completed ? null : completed });

  return (
    <div className="card mb-6 animate-fade-up-delay-2 p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
          <History className="h-4 w-4" />
        </span>
        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Plan history</h2>
        <span className="ml-auto text-xs font-medium text-zinc-400">
          Your answers shape tomorrow&apos;s plan
        </span>
      </div>

      <div className="divide-y divide-zinc-950/5 dark:divide-white/5">
        {past.map((p) => (
          <div key={p.day} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
            <div className="w-24 shrink-0 text-sm font-medium text-zinc-500 dark:text-zinc-400">
              {format(new Date(`${p.day}T12:00:00`), 'EEE, MMM d')}
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-zinc-900 dark:text-white">
                {p.recommendation.type}
                {p.source === 'ai' && (
                  <Sparkles className="h-3 w-3 shrink-0 text-indigo-500 dark:text-indigo-400" />
                )}
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {p.recommendation.duration} min · {p.recommendation.intensity} intensity
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleRating(p, 'up')}
                className={`rounded-lg p-1.5 transition ${
                  p.rating === 'up'
                    ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
                aria-label="Good plan"
                title="Good plan"
              >
                <ThumbsUp className="h-4 w-4" />
              </button>
              <button
                onClick={() => toggleRating(p, 'down')}
                className={`rounded-lg p-1.5 transition ${
                  p.rating === 'down'
                    ? 'bg-rose-600/10 text-rose-600 dark:bg-rose-400/10 dark:text-rose-400'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
                aria-label="Not for me"
                title="Not for me"
              >
                <ThumbsDown className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-xs text-zinc-400">Did it?</span>
              <button
                onClick={() => toggleCompleted(p, true)}
                className={`rounded-lg p-1.5 transition ${
                  p.completed === true
                    ? 'bg-emerald-600/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
                aria-label="Yes, did it"
                title="Yes"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={() => toggleCompleted(p, false)}
                className={`rounded-lg p-1.5 transition ${
                  p.completed === false
                    ? 'bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300'
                    : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                }`}
                aria-label="No, skipped it"
                title="No"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
